import { useEffect, useState } from "react";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  listBuilderAssistantCandidates,
  markBuilderAssistantCandidateReviewed,
  renameBuilderAssistantComponent,
} from "@/lib/manufacturing/builderAssistantClient";
import type {
  BuilderAssistantComponent,
  BuilderAssistantObservation,
} from "@/lib/manufacturing/builderAssistantTypes";

export function BuilderAssistantReview({
  components,
  onComponentsChanged,
}: {
  components: BuilderAssistantComponent[];
  onComponentsChanged: () => void;
}) {
  const [rows, setRows] = useState<BuilderAssistantObservation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});

  async function reload() {
    const res = await listBuilderAssistantCandidates();
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setRows(res.data.observations);
  }

  useEffect(() => {
    void reload();
  }, []);

  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Build-book candidates</h3>
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Reviewed means a person looked at it. It is not an approved standard.
        </p>
        {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        {rows.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">No unreviewed candidates.</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-2 text-sm">
                <span className="text-gray-900 dark:text-zinc-100">{row.description}</span>
                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() => {
                    void markBuilderAssistantCandidateReviewed(row.id).then(() => reload());
                  }}
                >
                  Mark reviewed
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Component names</h3>
        <ul className="space-y-2">
          {components.map((component) => (
            <li key={component.id} className="flex gap-2">
              <input
                className={inputClass}
                value={names[component.id] ?? component.name}
                onChange={(event) =>
                  setNames((prev) => ({ ...prev, [component.id]: event.target.value }))
                }
              />
              <button
                type="button"
                className={ghostBtnClass}
                onClick={() => {
                  const name = (names[component.id] ?? component.name).trim();
                  void renameBuilderAssistantComponent(component.id, name).then((res) => {
                    if (!res.ok) setError(res.error);
                    else onComponentsChanged();
                  });
                }}
              >
                Rename
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
